// src/pages/admin/FinanceOperation.jsx
import { setStoredAssociateFeePageSize } from "@utils";

import AssociateFee from "@pages/admin/AssociateFee";
import JournalEntry from "@pages/admin/JournalEntry";
import Check from "@pages/admin/Check";
import DailyDeposits from "@pages/admin/DailyDeposits";
import DepositSheet from "@pages/admin/DepositSheet";
import AdpNetsuite from "@pages/admin/AdpNetsuite";
import CashManagement from "@pages/cash_management/CashManagement";

/**
 * Finance Operations tab group.
 *
 * Presentational wrapper: renders the finance sub-tab bar and the body of the
 * selected sub-tab. State, URL persistence, month clamping and the header
 * selection cards live in the parent (Admin / Management).
 *
 * Clinic selections are passed in already resolved, so each parent decides how
 * an empty "All Clinics" selection expands (Admin: area-manager / summary codes;
 * Management: the user's assigned clinics).
 */
export default function FinanceOperation({
  // Sub-tab navigation
  financeTab,
  setFinanceTab,
  financeTabOptions,

  // Month / pay-period selection
  months,
  payPeriod,
  // Date for Cash Management
  cashDate,
  // { from, to } window for Daily Deposits
  dailyDepositsRange,

  // Paging (Associate Fee)
  page,
  setPage,
  associateFeePageSize,
  setAssociateFeePageSize,

  // Resolved clinic selections per sub-tab
  associateFeeClinics,
  associateFeeClinicCodes = null,
  journalEntryClinics,
  checkClinics,
  dailyDepositsClinics,
  depositSheetClinics,
  cashManagementClinics,
  cashManagementClinicOptions,

  // Permissions
  canSeeJournalEntry = true,
  canSeeCheck = true,
  canSeeDailyDeposits = true,
  canSeeDepositSheet = true,
  canSeeAdpNetsuite = true,

  // Optional content rendered between the sub-tab bar and the tab body
  // (e.g. the month / clinic selection bar).
  selectionSlot = null,
}) {
  return (
    <>
      <div className="mb-4 flex gap-1 border-b border-palette-slate-divider">
        {financeTabOptions.map((tabOption) => (
          <button
            key={tabOption.key}
            type="button"
            onClick={() => setFinanceTab(tabOption.key)}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm font-medium ${
              financeTab === tabOption.key
                ? "border-palette-indigo-label text-palette-indigo-badge"
                : "border-transparent text-palette-slate-secondary hover:text-palette-slate-heading"
            }`}
          >
            {tabOption.label}
          </button>
        ))}
      </div>

      {selectionSlot}

      {financeTab === "associateFee" && (
        <AssociateFee
          months={months}
          clinics={associateFeeClinics}
          clinicCodes={associateFeeClinicCodes}
          page={page}
          pageSize={associateFeePageSize}
          onPageChange={setPage}
          onPageSizeChange={(n) => {
            setPage(1);
            setAssociateFeePageSize(n);
            setStoredAssociateFeePageSize(n);
          }}
        />
      )}

      {financeTab === "journalEntry" && canSeeJournalEntry && (
        <JournalEntry months={months} clinics={journalEntryClinics} />
      )}

      {financeTab === "check" && canSeeCheck && <Check months={months} clinics={checkClinics} />}

      {financeTab === "dailyDeposits" && canSeeDailyDeposits && (
        <DailyDeposits range={dailyDepositsRange} clinics={dailyDepositsClinics} />
      )}

      {financeTab === "depositSheet" && canSeeDepositSheet && (
        <DepositSheet months={months} clinics={depositSheetClinics} />
      )}

      {financeTab === "adpNetsuite" && canSeeAdpNetsuite && (
        <AdpNetsuite month={payPeriod?.month || ""} payPeriod={payPeriod} />
      )}

      {financeTab === "cashManagement" && (
        <CashManagement
          date={cashDate}
          clinicOptions={cashManagementClinicOptions}
          clinics={cashManagementClinics}
        />
      )}
    </>
  );
}
